// Maps the voice agent's loose, spoken vocabulary ("warmer", "pop the shadows")
// onto the concrete grade schema used by the editors. Kept editor-agnostic so
// both the photo and video studios register identical handlers.
import { PRESETS, ADJUSTMENT_SCHEMA, applyGradeOps, applyLook, stampPreset } from '../utils/grade';

const SCHEMA_BY_KEY = new Map(ADJUSTMENT_SCHEMA.map((s) => [s.key, s]));

// Spoken control name -> schema key.
const CONTROL_ALIASES = {
  exposure: 'basic.exposure', brightness: 'basic.exposure', ev: 'basic.exposure', exposures: 'basic.exposure',
  contrast: 'basic.contrast',
  highlights: 'basic.highlights', highlight: 'basic.highlights', highs: 'basic.highlights',
  shadows: 'basic.shadows', shadow: 'basic.shadows',
  whites: 'basic.whites', white: 'basic.whites',
  blacks: 'basic.blacks', black: 'basic.blacks',
  temperature: 'wb.temp', temp: 'wb.temp', warmth: 'wb.temp', warm: 'wb.temp', warmer: 'wb.temp',
  cooler: 'wb.temp', white_balance: 'wb.temp', wb: 'wb.temp',
  tint: 'wb.tint', magenta: 'wb.tint',
  vibrance: 'presence.vibrance', vibrancy: 'presence.vibrance',
  saturation: 'presence.saturation', sat: 'presence.saturation', color: 'presence.saturation',
  clarity: 'presence.clarity', punch: 'presence.clarity',
  texture: 'presence.texture',
  dehaze: 'presence.dehaze', haze: 'presence.dehaze',
  denoise: 'presence.denoise', noise: 'presence.denoise', noise_reduction: 'presence.denoise', grain_reduction: 'presence.denoise',
  sharpness: 'effects.sharpness', sharpen: 'effects.sharpness', sharpening: 'effects.sharpness',
  vignette: 'effects.vignette', vignetting: 'effects.vignette',
  grain: 'effects.grain',
  glow: 'effects.glow', bloom: 'effects.glow',
  halation: 'effects.halation',
};

const getPath = (obj, path) => {
  let o = obj;
  for (const k of path) { if (o == null) return undefined; o = o[k]; }
  return o;
};

export function controlKey(name) {
  if (!name) return null;
  const n = String(name).toLowerCase().trim().replace(/[\s-]+/g, '_');
  if (CONTROL_ALIASES[n]) return CONTROL_ALIASES[n];
  if (SCHEMA_BY_KEY.has(n)) return n;
  // last resort: match against a schema label ("Skin protect", "Decode exposure")
  const byLabel = ADJUSTMENT_SCHEMA.find((s) => s.label.toLowerCase().replace(/[\s-]+/g, '_') === n);
  return byLabel ? byLabel.key : null;
}

// Returns { grade, applied } where applied is { key, label, value } or null.
export function adjustGrade(grade, { control, value, relative }) {
  const key = controlKey(control);
  if (!key) return { grade, applied: null };
  const def = SCHEMA_BY_KEY.get(key);
  let v = Number(value);
  if (!Number.isFinite(v)) return { grade, applied: null };
  // "cooler" means negative temperature even if the model says a positive number.
  if (String(control).toLowerCase().trim() === 'cooler' && v > 0) v = -v;
  if (relative) v = (Number(getPath(grade, def.path)) || 0) + v;
  v = Math.max(def.min, Math.min(def.max, v));
  return { grade: applyGradeOps(grade, [{ key, value: v }]), applied: { key, label: def.label, value: v } };
}

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Fuzzy-resolve a spoken look name to a preset { id, name, grade }.
export function resolveLook(name, extra = []) {
  if (!name) return null;
  const all = [...PRESETS.filter((p) => p.id !== 'none'), ...(extra || [])];
  const q = norm(name);
  if (!q) return null;
  let hit = all.find((p) => norm(p.name) === q);
  if (!hit) hit = all.find((p) => { const k = norm(p.name); return k.includes(q) || q.includes(k); });
  if (!hit) {
    const qt = String(name).toLowerCase().split(/\s+/).filter(Boolean);
    hit = all
      .map((p) => { const pt = String(p.name || '').toLowerCase().split(/\s+/); return { p, score: qt.filter((t) => pt.some((x) => x.includes(t))).length }; })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)[0]?.p;
  }
  return hit || null;
}

// Apply a resolved look onto the current grade (carries log-decode like the UI).
export function applyLookGrade(grade, preset) {
  return stampPreset(applyLook(grade, preset.grade), preset, 100);
}

export function presetNames(extra = []) {
  return [...PRESETS.filter((p) => p.id !== 'none'), ...(extra || [])].map((p) => p.name);
}
